import React from 'react';
import {
  ChartBarIcon,
  CogIcon,
  BoltIcon,
  CurrencyDollarIcon,
  ChartPieIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  WalletIcon,
  ShieldCheckIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface SidebarProps {
  isOpen: boolean;
  currentView?: string;
  onViewChange?: (viewId: string) => void;
  onClose?: () => void;
  isMobile?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, currentView = 'dashboard', onViewChange, onClose, isMobile = false }) => {
  const menuItems = [
    { id: 'dashboard', label: '대시보드', icon: ChartBarIcon },
    { id: 'trading', label: '거래', icon: CurrencyDollarIcon },
    { id: 'positions', label: '포지션', icon: WalletIcon },
    { id: 'analytics', label: '분석', icon: ChartPieIcon },
    { id: 'bot-control', label: '봇 제어', icon: BoltIcon },
    { id: 'strategies', label: '전략', icon: ArrowTrendingUpIcon },
    { id: 'backtest', label: '백테스트', icon: ClockIcon },

    { id: 'ai-chat', label: 'AI 어시스턴트', icon: ChatBubbleLeftRightIcon },
    { id: 'reports', label: '보고서', icon: DocumentTextIcon },
    { id: 'security', label: '보안', icon: ShieldCheckIcon },
    { id: 'settings', label: '설정', icon: CogIcon }
  ];

  const handleMenuClick = (menuId: string) => {
    console.log(`✅ ${menuId} 페이지로 이동합니다`);
    
    // 실제 뷰 변경 함수 호출
    if (onViewChange) {
      onViewChange(menuId);
    }
  };

  return (
    <aside className={`
      fixed left-0 top-0 h-full w-64 bg-white dark:bg-gray-900 border-r border-gray-300 dark:border-gray-700 z-40
      transition-transform duration-300 ease-in-out
      ${isOpen ? 'translate-x-0' : '-translate-x-full'}
    `}>
      <div className="flex flex-col h-full pt-16">
        {/* 닫기 버튼 */}
        <div className="absolute top-4 right-4 z-50">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="메뉴 닫기"
          >
            <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
        {/* 프로필 영역 */}
        <div className="p-4 border-b border-gray-300 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">📊</span>
            </div>
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">자동 거래 시스템</p>
              <p className="text-xs text-gray-500 dark:text-gray-500">실시간 분석 중</p>
            </div>
          </div>
        </div>

        {/* 빠른 상태 */}
        <div className="px-4 py-3 border-b border-gray-300 dark:border-gray-700">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">빠른 상태</div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-gray-700 dark:text-gray-300">총 잔고</span>
              <span className="text-xs text-gray-900 dark:text-white font-medium">$12,485.50</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-gray-700 dark:text-gray-300">오늘 수익</span>
              <span className="text-xs text-green-600 dark:text-green-500 font-medium">+1.2%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-gray-700 dark:text-gray-300">열린 포지션</span>
              <span className="text-xs text-gray-900 dark:text-white font-medium">3</span>
            </div>
          </div>
        </div>

        {/* 네비게이션 메뉴 */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {menuItems.map((item, index) => (
              <li key={index}>
                <button
                  onClick={() => handleMenuClick(item.id)}
                  className={`
                    w-full flex items-center p-3 rounded-lg transition-all duration-200 text-left
                    ${currentView === item.id
                      ? 'bg-blue-600 text-white shadow-lg transform scale-105' 
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white hover:transform hover:scale-102'
                    }
                  `}
                >
                  <item.icon className={`h-5 w-5 mr-3 ${
                    currentView === item.id ? 'text-white' : 'text-gray-600 dark:text-gray-400'
                  }`} />
                  <span className="text-sm font-medium">{item.label}</span>
                  {currentView === item.id && (
                    <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* 하단 사용자 정보 */}
        <div className="p-4 border-t border-gray-300 dark:border-gray-700">
          <div className="flex items-center mb-3">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mr-3">
              <span className="text-white text-sm font-medium">U</span>
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-900 dark:text-white font-medium">사용자</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">프리미엄 계정</p>
            </div>
          </div>
          <button 
            onClick={() => {
              // eslint-disable-next-line no-restricted-globals
              if (confirm('정말 로그아웃 하시겠습니까?')) {
                console.log('🚪 로그아웃 처리');
                alert('로그아웃 되었습니다.');
              }
            }}
            className="w-full flex items-center justify-center p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
            <span className="text-sm">로그아웃</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar; 